import { DataSetsProps } from '@/types';

type DatasetScope = {
  loja: number | null | undefined;
  segmento: number | null | undefined;
};

type ScopeRow = Record<string, any>;

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const getFirstPresentValue = (row: ScopeRow, keys: string[]): unknown => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      return row[key];
    }
  }
  return undefined;
};

export const parseScopeNumber = (value: unknown): number | null | undefined => {
  if (value === undefined || value === '') return undefined;
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getDatasetScope = (dataset: Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>): DatasetScope => ({
  loja: parseScopeNumber(dataset.COD_LOJA),
  segmento: parseScopeNumber(dataset.COD_SEGMENTO),
});

export const getRowScope = (row: ScopeRow): DatasetScope => {
  if (!isPlainObject(row)) {
    return {
      loja: null,
      segmento: null,
    };
  }

  const lojaCandidate = getFirstPresentValue(row, [
    'COD_LOJA',
    'cod_loja',
    'NROEMPRESA',
    'nroempresa',
    'COD_EMPRESA',
    'cod_empresa',
  ]);
  const segmentoCandidate = getFirstPresentValue(row, [
    'COD_SEGMENTO',
    'cod_segmento',
    'NRO_SEGMENTO',
    'nro_segmento',
    'NROSEGMENTO',
    'nrosegmento',
  ]);

  return {
    loja: parseScopeNumber(lojaCandidate),
    segmento: parseScopeNumber(segmentoCandidate),
  };
};

export const rowMatchesScope = (row: ScopeRow, scope: DatasetScope): boolean => {
  const rowScope = getRowScope(row);
  if (scope.loja !== undefined && rowScope.loja !== scope.loja) return false;
  if (scope.segmento !== undefined) {
    // Alguns payloads não propagam COD_SEGMENTO para cada item; nesse caso
    // usamos apenas COD_LOJA para manter o comportamento incremental correto.
    if (rowScope.segmento !== undefined && rowScope.segmento !== scope.segmento) return false;
  }
  return scope.loja !== undefined || scope.segmento !== undefined;
};

export const mergeDataValues = (current: any, incoming: any): any => {
  if (current == null) return incoming;
  if (incoming == null) return current;

  if (Array.isArray(current) && Array.isArray(incoming)) {
    return [...current, ...incoming];
  }

  if (isPlainObject(current) && isPlainObject(incoming)) {
    const merged: Record<string, any> = { ...current };
    Object.keys(incoming).forEach((key) => {
      merged[key] = key in merged ? mergeDataValues(merged[key], incoming[key]) : incoming[key];
    });
    return merged;
  }

  return incoming;
};

/**
 * Loja efetiva do item para alinhar chaves ao merge do catálogo (raiz, depois 1ª embalagem/preço).
 */
export const getCatalogScopeLoja = (product: any): number | null | undefined => {
  const fromRoot = parseScopeNumber(product?.COD_LOJA);
  if (fromRoot !== undefined) return fromRoot;
  if (Array.isArray(product?.EMBALAGEM) && product.EMBALAGEM.length > 0) {
    const fromEmb = parseScopeNumber(product.EMBALAGEM[0]?.COD_LOJA);
    if (fromEmb !== undefined) return fromEmb;
  }
  if (Array.isArray(product?.PRECO) && product.PRECO.length > 0) {
    const fromPrice = parseScopeNumber(product.PRECO[0]?.COD_LOJA);
    if (fromPrice !== undefined) return fromPrice;
  }
  return undefined;
};

/**
 * Chave para merge incremental de arrays no sync parcial: substitui apenas linhas cujo par
 * (produto + loja + segmento quando existir) aparece no batch novo, mantendo o restante da mesma loja.
 * Sem COD_PRODUTO identificável retorna null (fallback para comportamento legado por escopo).
 */
export const partialSyncArrayRowKey = (row: ScopeRow): string | null => {
  if (!isPlainObject(row)) return null;
  const code = Number(getFirstPresentValue(row, ['COD_PRODUTO', 'cod_produto']));
  if (!Number.isFinite(code)) return null;
  const base = `${code}::${String(getCatalogScopeLoja(row))}`;
  const rs = getRowScope(row);
  if (rs.segmento === undefined) return base;
  return `${base}::seg:${String(rs.segmento)}`;
};

export const mergeArrayByDatasetScope = (
  existing: any,
  incoming: any,
  scopes: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any[] => {
  const prev = Array.isArray(existing) ? existing : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!next.length) return prev;

  const normalizedScopes = scopes.map(getDatasetScope);
  const scoped = normalizedScopes.filter((scope) => scope.loja !== undefined || scope.segmento !== undefined);
  if (!scoped.length) {
    return [...prev, ...next];
  }

  const incomingKeys = new Set(
    next.map((row) => partialSyncArrayRowKey(row)).filter((key): key is string => key != null)
  );

  const matchesUpdatedScope = (row: any) => scoped.some((scope) => rowMatchesScope(row, scope));

  // Payload sem linhas identificáveis por produto: mantém substituição completa do escopo (snapshot por loja).
  if (incomingKeys.size === 0) {
    const keptLegacy = prev.filter((row) => !matchesUpdatedScope(row));
    return [...keptLegacy, ...next];
  }

  const kept = prev.filter((row) => {
    if (!matchesUpdatedScope(row)) return true;
    const key = partialSyncArrayRowKey(row);
    if (key == null) return true;
    return !incomingKeys.has(key);
  });

  return [...kept, ...next];
};

export const mergeByDatasetScope = (
  existing: any,
  incoming: any,
  datasets: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any => {
  if (incoming == null) return existing;
  if (existing == null) return incoming;

  if (Array.isArray(existing) || Array.isArray(incoming)) {
    return mergeArrayByDatasetScope(existing, incoming, datasets);
  }

  if (isPlainObject(existing) && isPlainObject(incoming)) {
    return mergeDataValues(existing, incoming);
  }

  return incoming;
};

export const shouldDownloadUpdatedDataset = (
  oldDataset: Pick<DataSetsProps, 'DT_UPDATE'> | undefined,
  newDataset: Pick<DataSetsProps, 'DT_UPDATE'>
): boolean => oldDataset?.DT_UPDATE !== newDataset.DT_UPDATE;

const toArray = <T = any>(value: any): T[] => (Array.isArray(value) ? value : []);

const mergeClientLojasByScope = (current: any[] | undefined, incoming: any[] | undefined): any[] => {
  const byKey = new Map<string, any>();
  [...toArray(current), ...toArray(incoming)].forEach((loja) => {
    if (!loja || typeof loja !== 'object') return;
    const segmentos = Array.isArray(loja.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    const segmentosKey = segmentos
      .map((segmento: any) => Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento))
      .filter((n: number): n is number => Number.isFinite(n))
      .join(',');
    const key = `${Number(loja.COD_LOJA || 0)}:${segmentosKey}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...loja });
      return;
    }
    byKey.set(key, { ...prev, ...loja });
  });
  return Array.from(byKey.values());
};

export const mergeClientsByCodePreserveStores = (existing: any, incoming: any): any[] => {
  const prev = toArray(existing);
  const next = toArray(incoming);
  if (!next.length) return prev;

  const byClient = new Map<string, any>();
  const clientKey = (client: any) => {
    const cod = Number(client?.COD_CLIENTE);
    if (!Number.isFinite(cod)) return null;
    const loja = parseScopeNumber(client?.COD_LOJA);
    return `${cod}::${String(loja)}`;
  };
  prev.forEach((client) => {
    const key = clientKey(client);
    if (!key) return;
    byClient.set(key, client);
  });

  next.forEach((client) => {
    const key = clientKey(client);
    if (!key) return;
    const currentClient = byClient.get(key);
    if (!currentClient) {
      byClient.set(key, client);
      return;
    }

    byClient.set(key, {
      ...currentClient,
      ...client,
      lojas: mergeClientLojasByScope(currentClient.lojas, client.lojas),
    });
  });

  return Array.from(byClient.values());
};

export const mergeCatalogByProductCode = (existing: any, incoming: any): any[] => {
  const prev = toArray(existing);
  const next = toArray(incoming);
  if (!next.length) return prev;

  const productKeys = (product: any) => {
    const code = Number(product?.COD_PRODUTO);
    if (!Number.isFinite(code)) return null;

    const k = new Set<string>();
    const stores = product?.EMBALAGEM || product?.PRECO || [];
    
    if (Array.isArray(stores)) {
      stores.forEach((store: any) => {
        k.add(`${code}::${store.COD_LOJA}`);
      });
    }
    return Array.from(k);
  };

  const byProduct = new Map<string, any>();
  
  [...next, ...prev].forEach((product) => {
    const keys = productKeys(product);
    if (!keys || !keys.length) return;
    keys.forEach((key: any) => {
      if (byProduct.has(key)) return;
      byProduct.set(key, product);
    });
  });

  return Array.from(byProduct.values());
};

export const mergePrecoByScope = (
  existing: any,
  incoming: any,
  _datasets: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any => {
  const prev = isPlainObject(existing) ? existing : {};
  const next = isPlainObject(incoming) ? incoming : incoming;
  if (!isPlainObject(next)) return next;

  // Apenas usado em sync parcial (vários arquivos por tipo). Deep-merge preserva lojas/segmentos/produtos
  // ausentes do payload; o apagamento prévio do ramo causava perda de preços quando o arquivo era delta.
  return mergeDataValues(prev, next);
};

const isPromocaoClienteLeafNode = (value: any): boolean =>
  isPlainObject(value) && Number.isFinite(Number(value.COD_PRODUTO)) && 'VLR_PRECO' in value;

/**
 * Converte o array do dataset em árvore [COD_CLIENTE][QTD_EMBALAGEM][COD_PRODUTO][COD_LOJA][NRO_SEGMENTO].
 * Em duplicata no mesmo batch, mantém o menor VLR_PRECO (mesma regra do download).
 */
export const reducePromocaoClienteArrayToNestedMap = (items: any[]): Record<string, any> => {
  if (!Array.isArray(items) || !items.length) return {};
  return items.reduce((acc: Record<string, any>, p: any) => {
    if (!isPlainObject(p)) return acc;
    const clientPrice = acc[p.COD_CLIENTE];

    if (!clientPrice) {
      acc[p.COD_CLIENTE] = { [p.QTD_EMBALAGEM]: { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } } };
      return acc;
    }

    const qtdEmbalagem = clientPrice[p.QTD_EMBALAGEM];
    if (!qtdEmbalagem) {
      acc[p.COD_CLIENTE][p.QTD_EMBALAGEM] = { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } };
      return acc;
    }

    const productPrice = qtdEmbalagem[p.COD_PRODUTO];
    if (!productPrice) {
      acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO] = { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } };
      return acc;
    }

    const productPriceLoja = productPrice[p.COD_LOJA];
    if (!productPriceLoja) {
      acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA] = { [p.NRO_SEGMENTO]: p };
      return acc;
    }

    const productPriceSegmento = productPriceLoja[p.NRO_SEGMENTO];
    if (!productPriceSegmento) {
      acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p;
      return acc;
    }

    if (p.VLR_PRECO < productPriceSegmento.VLR_PRECO) {
      acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p;
    }

    return acc;
  }, {});
};

/**
 * Merge profundo da árvore de preço promocional: ramos só presentes no payload são sobrescritos;
 * folhas combinam campos (payload parcial não apaga campos antigos não enviados).
 */
export const mergePrecoPromocaoClienteNestedMaps = (prev: any, next: any): any => {
  if (next == null) return prev;
  if (prev == null) return next;
  if (isPromocaoClienteLeafNode(prev) || isPromocaoClienteLeafNode(next)) {
    if (isPromocaoClienteLeafNode(next)) {
      return isPromocaoClienteLeafNode(prev) ? { ...prev, ...next } : next;
    }
    return prev;
  }
  if (!isPlainObject(prev) || !isPlainObject(next)) return next;

  const merged: Record<string, any> = { ...prev };
  Object.keys(next).forEach((key) => {
    merged[key] = key in prev ? mergePrecoPromocaoClienteNestedMaps(prev[key], next[key]) : next[key];
  });
  return merged;
};

export const normalizePrecoPromocaoClienteToMap = (value: any): Record<string, any> => {
  if (Array.isArray(value)) return reducePromocaoClienteArrayToNestedMap(value);
  if (isPlainObject(value)) return value;
  return {};
};

/** Sync parcial: IndexedDB guarda mapa aninhado; o download vem em array — merge genérico zerava o “existing”. */
export const mergePrecoPromocaoClientePartial = (existing: any, incoming: any): Record<string, any> => {
  const prevMap = normalizePrecoPromocaoClienteToMap(existing);
  const nextMap = normalizePrecoPromocaoClienteToMap(incoming);
  return mergePrecoPromocaoClienteNestedMaps(prevMap, nextMap);
};

/** Chave de loja no mapa agregado (`null` / default → `'null'`). */
export const parametroPrecoBaseLojaKey = (loja: unknown): string => {
  if (loja === undefined || loja === '') return 'null';
  if (loja === null) return 'null';
  const n = Number(loja);
  return Number.isFinite(n) ? String(n) : 'null';
};

const isParametroPrecoBaseLojaRootMap = (obj: Record<string, any>): boolean => {
  const keys = Object.keys(obj);
  if (!keys.length) return false;
  return keys.every((k) => k === 'null' || /^\d+$/.test(k));
};

/**
 * Agrupa `parametro_preco_base` por COD_LOJA: formato árvore `[loja][tab][cond][faixa]` ou array de linhas.
 */
export const toParametroPrecoBaseLojaMap = (value: any): Record<string, any> => {
  if (value == null) return {};
  if (Array.isArray(value)) {
    const m: Record<string, any[]> = {};
    for (const row of value) {
      if (!isPlainObject(row)) continue;
      const k = parametroPrecoBaseLojaKey((row as any).COD_LOJA);
      if (!m[k]) m[k] = [];
      m[k].push(row);
    }
    return m;
  }
  if (isPlainObject(value) && isParametroPrecoBaseLojaRootMap(value as Record<string, any>)) {
    return { ...(value as Record<string, any>) };
  }
  return {};
};

const mergeParametroPrecoLojaBranch = (prev: any, next: any): any => {
  if (next == null) return prev;
  if (prev == null) return next;
  if (Array.isArray(prev) && Array.isArray(next)) return next;
  if (isPlainObject(prev) && isPlainObject(next) && !Array.isArray(prev) && !Array.isArray(next)) {
    return mergeDataValues(prev, next);
  }
  return next;
};

/** Converte subárvore [NROTABVENDA][NROCONDICAOPAGTO][FAIXA] → linhas (mesma convenção de `normalizeParametroPrecoBase`). */
const flattenParametroPrecoLojaTree = (codLoja: number | null, tabMap: Record<string, any>): any[] => {
  const result: any[] = [];
  if (!isPlainObject(tabMap) || Array.isArray(tabMap)) return result;
  for (const [tab, condMap] of Object.entries(tabMap)) {
    if (!isPlainObject(condMap) || Array.isArray(condMap)) continue;
    for (const [cond, faixaMap] of Object.entries(condMap)) {
      if (!isPlainObject(faixaMap) || Array.isArray(faixaMap)) continue;
      for (const [faixa, leaf] of Object.entries(faixaMap)) {
        if (!isPlainObject(leaf) || Array.isArray(leaf)) continue;
        const L = leaf as Record<string, unknown>;
        const perE = L.peracrentrega ?? L.PERACRENTREGA;
        const perF = L.peracrfinanceiro ?? L.PERACRFINANCEIRO;
        if (perE === undefined && perF === undefined) continue;
        result.push({
          NROTABVENDA: String(tab),
          NROCONDICAOPAGTO: Number(cond),
          FAIXAACRFINANCEIRO: faixa,
          COD_LOJA: codLoja,
          PERACRENTREGA: Number(perE ?? 0),
          PERACRFINANCEIRO: Number(perF ?? 0),
        });
      }
    }
  }
  return result;
};

const flattenParametroPrecoMergedMapToArray = (map: Record<string, any>): any[] => {
  const rows: any[] = [];
  for (const [lojaKey, branch] of Object.entries(map)) {
    if (Array.isArray(branch)) {
      rows.push(...branch);
      continue;
    }
    if (!isPlainObject(branch)) continue;
    const codLoja = lojaKey === 'null' ? null : Number(lojaKey);
    rows.push(...flattenParametroPrecoLojaTree(Number.isFinite(codLoja as number) ? (codLoja as number) : null, branch));
  }
  return rows;
};

const mergedMapOnlyObjectSubtrees = (map: Record<string, any>): boolean =>
  Object.values(map).every((v) => v != null && isPlainObject(v) && !Array.isArray(v));

/**
 * Sync parcial com vários arquivos `parametro_preco_base` por loja: só substitui o ramo da loja presente no payload.
 * Evita o merge genérico (array+objeto → “existing” vazio) e preserva 635/590 quando baixa só o default.
 */
export const mergeParametroPrecoBasePartial = (existing: any, incoming: any): any => {
  if (incoming == null) return existing;
  if (existing == null) return incoming;

  const exMap = toParametroPrecoBaseLojaMap(existing);
  const inMap = toParametroPrecoBaseLojaMap(incoming);

  if (!Object.keys(exMap).length && !Object.keys(inMap).length) {
    return mergeDataValues(
      isPlainObject(existing) ? existing : {},
      isPlainObject(incoming) ? incoming : {}
    );
  }

  const merged: Record<string, any> = { ...exMap };
  Object.keys(inMap).forEach((k) => {
    merged[k] = mergeParametroPrecoLojaBranch(exMap[k], inMap[k]);
  });

  const useFlatArray =
    Array.isArray(existing) ||
    Array.isArray(incoming) ||
    !mergedMapOnlyObjectSubtrees(merged);

  if (useFlatArray) {
    return flattenParametroPrecoMergedMapToArray(merged);
  }

  return merged;
};
